import { effect, inject, Injector, runInInjectionContext } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { UserSessionService } from '../services/user-session.service';
import { AuthorizationStateService } from '../services/authorization/authorization-state.service';
import { AuthService } from '@auth0/auth0-angular';
import { firstValueFrom } from 'rxjs';
import { GuardConstants, SystemPageRoutes } from '../constants/constants';
import { MaintenanceService } from '../services/maintenance.service';
import { UserStatuses } from '../enums/data.enums';

export const userLoadedGuard: CanActivateFn = async (_route, _state) => {
  const userSessionService = inject(UserSessionService);
  const authorizationStateService = inject(AuthorizationStateService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const injector = inject(Injector);
  const maintenanceService = inject(MaintenanceService);

  try {
    const isLoading = await firstValueFrom(authService.isLoading$);
    if (isLoading) {
      await waitForAuth0Ready(authService);
    }

    let activeUser = userSessionService.activeUser();
    
    if (!activeUser) {
      await userSessionService.setActiveUser();
      activeUser = userSessionService.activeUser();
    }

    if (!activeUser) {
      if (maintenanceService.isInMaintenance()) {
        return router.createUrlTree(SystemPageRoutes.MAINTENANCE());
      }

      console.warn('userLoadedGuard: Could not load active user');
      return router.createUrlTree(SystemPageRoutes.AUTH_ERROR());
    }

    // Auth0 refuses a deactivated user's sign-in, but a token issued before the deactivation stays valid
    // until it expires, so the user record is the only thing that can say no in the meantime.
    if (activeUser.status === UserStatuses.Inactive) {
      return router.createUrlTree(SystemPageRoutes.ACCOUNT_DEACTIVATED());
    }

    const authorizationState = await waitForAuthorizationState(
      authorizationStateService,
      GuardConstants.AUTHORIZATION_STATE_TIMEOUT_MS(),
      injector
    );

    if (!authorizationState) {
      if (maintenanceService.isInMaintenance()) {
        return router.createUrlTree(SystemPageRoutes.MAINTENANCE());
      }

      return router.createUrlTree(SystemPageRoutes.AUTH_ERROR());
    }

    return true;
  } catch (error) {
    if (maintenanceService.isInMaintenance()) {
      return router.createUrlTree(SystemPageRoutes.MAINTENANCE());
    }

    console.error('userLoadedGuard: Error loading user or authorization context:', error);
    return router.createUrlTree(SystemPageRoutes.AUTH_ERROR());
  }
};

async function waitForAuth0Ready(authService: AuthService): Promise<void> {
  return new Promise((resolve) => {
    const subscription = authService.isLoading$.subscribe((isLoading) => {
      if (!isLoading) {
        subscription.unsubscribe();
        resolve();
      }
    });
  });
}

async function waitForAuthorizationState(
  authorizationStateService: AuthorizationStateService,
  timeoutMs: number,
  injector: Injector
): Promise<any> {
  return new Promise((resolve) => {
    let effectRef: any;
    let timeoutId: any;

    timeoutId = setTimeout(() => {
      if (effectRef) {
        effectRef.destroy();
      }
      console.error('userLoadedGuard: Authorization context failed to load within timeout');
      resolve(null);
    }, timeoutMs);

    runInInjectionContext(injector, () => {
      effectRef = effect(() => {
        const authorizationState = authorizationStateService.authorizationState();

        // The service raises this once the load has definitively failed (retries exhausted, or a
        // terminal status), so there is nothing left to wait for — bail out now rather than sitting
        // out the remainder of the timeout.
        const loadFailed = authorizationStateService.authorizationLoadFailed();
        
        if (authorizationState !== null && authorizationState !== undefined) {
          clearTimeout(timeoutId);
          effectRef.destroy();
          resolve(authorizationState);
          return;
        }

        if (loadFailed) {
          clearTimeout(timeoutId);
          effectRef.destroy();
          console.error('userLoadedGuard: Authorization context failed to load');
          resolve(null);
        }
      });
    });
  });
}
